import { z } from 'zod';
import { IAsset } from './asset';
import { CurrencyCode } from './base';
import { CustomizationCategory } from './order';
import { ICategory, SkuGender } from './skus';
import { IBrand, IVendor } from './vendors';

export enum ProductStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const ProductStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'INACTIVE']);

export interface IProduct {
  id: number;
  lkStyleNumbers: Array<string>;
  name: string;
  labelName: string;
  gender?: SkuGender;
  status: ProductStatus;
  shopifyHandle: string;
  shopifyId: string;
  vendor: IVendor;
  brand: IBrand;
  brandName: string;
  vendorId: number;
  components: string;
  productType: ICategory;
  productTypeId: number;
  variants: Array<IProductVariant>;
  options: Array<IProductOption>;
  defaultImage: IAsset;
  images: Array<SKUAssetEntry>;
  customizationCategory: CustomizationCategory;
  query: string;
  lastShopifySyncedOn: string; // ZonedDateTime converted to ISO string
  pdpUrl?: string;
}

export interface IProductOption {
  name: string;
  values: Array<string>;
  optionsValues: Array<IProductOptionValue>;
  position: number;
}

export interface IProductOptionValue {
  displayName: string;
  colorCodes: Array<string>;
  imageUrl: string;
}

export interface IProductVariant {
  id: number;
  option: { [key: string]: string };
  size: string;
  color: string;
  gender?: SkuGender;
  colorHashcode: string;
  skuId: number;
  vendorId: number;
  vendorSkuCode: string;
  shopifyId: string;
  shopifyInventoryItemId: string;
  shopifyInventoryLevelId: string;
  skuCode: string;
  currency: CurrencyCode;
  barcode: string;
  sellingPrice: number;
  mtoSellingPrice: number;
  mrp: number;
  taxClassPerc: number;
  exchangeable: boolean;
  returnable: boolean;
  brandName: string;
  hsnCode: string;
  lkStyleNumber: string;
  enabled: boolean;

  jitFacilities: Array<number>;
  images: Array<SKUAssetEntry>;
  facilityInventory: { [key: number]: IFacilityInventory };
  inventorySummary: { [key: string]: number };
  mtoAcceptedByBrand: boolean;
  mtcAcceptedByBrand: boolean;
  mtoTAT: number;
  mtcTAT: number;
  vendorDiscount: number;
  platformDiscount: number;
  totalDiscount: number;
  washCareDescription: string;
  washType: string;
  productCare: string;
  vendorArticleNumber: string;
  mtoReturnable: boolean;
  mtcReturnable: boolean;
  aiDescription: string;
  season?: string;
  availableFacilities: Array<number>;
  inStockFacilities: Array<string>;
  inStockInBackFacilities: Array<string>;
  inStockInFrontFacilities: Array<string>;
}

export interface IFacilityInventory {
  availableInventory: number;
  frontInventory?: number;
  backInventory?: number;
  incomingQuantity?: number;
  outgoingQuantity?: number;
  blockedInventory?: number;
}

export interface SKUAssetEntry extends IAsset {
  shopifyMediaId: string;
  originalUrl: string;
}

export const CustomizationCategorySchema = z.enum([
  'DRESS',
  'TOP_WEAR',
  'BOTTOM_WEAR',
  'CO_ORD_SET',
  'NONE',
]);

export type CustomizationCategoryType = z.infer<typeof CustomizationCategorySchema>;

export const ProductDTOSchema = z.object({
  id: z.number(),
  itemGroupId: z.string(),
  handle: z.string(),
  title: z.string(),
  description: z.string(),
  brandName: z.string(),
  gender: z.enum(['MEN', 'WOMEN', 'UNISEX']).optional(),
  productType: z.string(),
  category: z.string(),
  subCategory: z.string(),
  variants: z.array(
    z.object({
      option: z.union([
        z.object({ SIZE: z.string(), COLOR: z.string() }),
        z.record(z.string(), z.string()),
      ]),
      size: z.string(),
      color: z.string(),
      gender: z.enum(['MEN', 'WOMEN', 'UNISEX']).optional(),
      skuCode: z.string(),
      currency: z.string(),
      barcode: z.string(),
      sellingPrice: z.number(),
      mrp: z.number(),
      vaultPrice: z.number(),
      enabledForOnline: z.boolean(),
      exchangeable: z.boolean(),
      returnable: z.boolean(),
      hsnCode: z.string(),
      lkStyleNumber: z.string(),
      isAvailableForSale: z.boolean(),
      isReadyToShip: z.boolean(),
      availableFacilities: z.array(z.unknown()),
      inStockFacilities: z.array(z.unknown()),
      inStockInFrontFacilities: z.array(z.unknown()),
      inStockInBackFacilities: z.array(z.unknown()),
      jitFacilities: z.array(z.unknown()),
      mtoAcceptedByBrand: z.boolean(),
      mtcAcceptedByBrand: z.boolean(),
      facilityInventory: z.record(
        z.string(),
        z.object({
          availableInventory: z.number(),
          frontInventory: z.number().optional(),
          backInventory: z.number().optional(),
          incomingQuantity: z.number().optional(),
          outgoingQuantity: z.number().optional(),
          blockedInventory: z.number().optional(),
        }),
      ),
      assets: z.array(
        z.object({
          type: z.string(),
          url: z.string(),
          uploaded: z.boolean(),
          ready: z.boolean(),
        }),
      ),
      jitTAT: z.number(),
      mtoTAT: z.number(),
      mtcTAT: z.number(),
      totalDiscount: z.number(),
      returnPeriodInDays: z.number(),
      exchangePeriodInDays: z.number(),
      availability: z.string(),
      colorHashcode: z.string().optional(),
      skuId: z.number().optional(),
      vendorId: z.number().optional(),
      vendorSkuCode: z.string().optional(),
      shopifyId: z.string().optional(),
      shopifyInventoryItemId: z.string().optional(),
      shopifyInventoryLevelId: z.string().optional(),
      mtoSellingPrice: z.number().optional(),
      taxClassPerc: z.number().optional(),
      brandName: z.string().optional(),
      enabled: z.boolean().optional(),
      images: z
        .array(
          z.object({
            url: z.string(),
            id: z.number().optional(),
            name: z.string().optional(),
            type: z.union([z.string(), z.number()]).optional(),
            usage: z.string().optional(),
            uploaded: z.boolean().optional(),
            shopifyMediaId: z.string(),
            originalUrl: z.string(),
          }),
        )
        .optional(),
      inventorySummary: z.record(z.string(), z.number()).optional(),
      vendorDiscount: z.number().optional(),
      platformDiscount: z.number().optional(),
      washCareDescription: z.string().optional(),
      washType: z.string().optional(),
      productCare: z.string().optional(),
      vendorArticleNumber: z.string().optional(),
      mtoReturnable: z.boolean().optional(),
      mtcReturnable: z.boolean().optional(),
      aiDescription: z.string().optional(),
      season: z.string().optional(),
    }),
  ),
  options: z.array(
    z.object({
      name: z.string(),
      values: z.array(z.string()),
      optionsValues: z.array(
        z.object({
          displayName: z.string(),
          colorCodes: z.array(z.string()).optional(),
          imageUrl: z.string().optional(),
        }),
      ),
      position: z.number(),
    }),
  ),
  sizeChart: z.object({
    measurements: z.array(z.string()),
    sizes: z.array(z.string()),
    sizeData: z.array(
      z.object({
        size: z.string(),
        measurements: z.object({
          BUST: z.string(),
          HIPS: z.string(),
          WAIST: z.string(),
        }),
      }),
    ),
    sizeChartImageAssetUrl: z.string(),
  }),
  occasions: z.string(),
  season: z.string(),
  merchType: z.string(),
  pattern: z.string(),
  fabric: z.string(),
  sleeveLength: z.string(),
  dressSkirtLength: z.string(),
  fit: z.string(),
  materialAndCareHTML: z.string(),
  componentsQty: z.string(),
  countryOfOrigin: z.string(),
  descriptionOfProductAttachments: z.string(),
  dimensions: z.string(),
  pockets: z.string(),
  numCompartments: z.string(),
  closure: z.string(),
  strapMaterial: z.string(),
  lining: z.boolean(),
  neckType: z.string(),
  liningFabric: z.string(),
  relatedLinks: z.array(
    z.object({
      path: z.string(),
      text: z.string(),
    }),
  ),
  supplierInfoHTML: z.string(),
  productDetailsHtml: z.string(),
  similarCollections: z.array(
    z.object({
      collectionHandle: z.string(),
      title: z.string(),
      type: z.string(),
    }),
  ),
  brandCollectionHandle: z.string(),
  labelTag: z.string(),
  termsAndPoliciesHtml: z.string(),
  isInternational: z.boolean(),
  shareLink: z.string(),
  availableCoupons: z.array(
    z.object({
      title: z.string(),
      code: z.string(),
      subTitle: z.string(),
      offerText: z.string(),
      firstOrderOnly: z.boolean(),
      enabled: z.boolean(),
      discount: z.number(),
      isPercentage: z.boolean(),
    }),
  ),
  lkStyleNumbers: z.array(z.string()).optional(),
  name: z.string().optional(),
  labelName: z.string().optional(),
  status: ProductStatusSchema.optional(),
  shopifyHandle: z.string().optional(),
  shopifyId: z.string().optional(),
  vendor: z.any().optional(),
  brand: z.any().optional(),
  vendorId: z.number().optional(),
  components: z.string().optional(),
  productTypeId: z.number().optional(),
  defaultImage: z
    .object({
      url: z.string(),
      id: z.number().optional(),
      name: z.string().optional(),
      type: z.union([z.string(), z.number()]).optional(),
      usage: z.string().optional(),
      uploaded: z.boolean().optional(),
    })
    .optional(),
  images: z
    .array(
      z.object({
        url: z.string(),
        id: z.number().optional(),
        name: z.string().optional(),
        type: z.union([z.string(), z.number()]).optional(),
        usage: z.string().optional(),
        uploaded: z.boolean().optional(),
        shopifyMediaId: z.string(),
        originalUrl: z.string(),
      }),
    )
    .optional(),
  customizationCategory: CustomizationCategorySchema.optional(),
  query: z.string().optional(),
  lastShopifySyncedOn: z.string().optional(),
  pdpUrl: z.string().optional(),
});

export type ProductDTOType = z.infer<typeof ProductDTOSchema>;
